// pages/Home.jsx
import React, { useState, useEffect } from "react";
import { apiFetch } from "../api";
import { useAuth } from "../context/AuthContext";
import { getAvatarUrl } from "../constants/avatars";
import { Link } from "react-router-dom";
import "../styles/pages/_home.css"
import { timeAgo, formatDate, formatDateTime, TimeAgo } from "../helpers/time";

export default function Home() {
    const { user, loading: authLoading } = useAuth();

    const [homeData, setHomeData] = useState(null);
    const [homeLoading, setHomeLoading] = useState(true);

    useEffect(() => {
        fetchHomeData();
    }, []);

    async function fetchHomeData() {
        try {
            const data = await apiFetch("/api/home");
            setHomeData(data);
        } catch (err) {
            console.error("Failed to fetch home data:", err);
        } finally {
            setHomeLoading(false);
        }
    }

    return (
        <div className="wrapper">
            <h1>Welcome to the Forum</h1>

            {homeLoading ? (
                <p>Loading…</p>
            ) : homeData ? (
                <>
                    {/* Forum Statistics Bar */}
                    <section className="home-stats">
                        <div className="stat-item">
                            <span className="stat-number">{homeData.stats.total_users}</span>
                            <span className="stat-label">Members</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-number">{homeData.stats.total_threads}</span>
                            <span className="stat-label">Threads</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-number">{homeData.stats.total_posts}</span>
                            <span className="stat-label">Posts</span>
                        </div>
                    </section>

                    <div className="home-grid">
                        {/* Recent Active Threads */}
                        <section className="home-section home-section--main">
                            <h2>Recent Activity</h2>
                            {homeData.recentActive.length === 0 ? (
                                <p className="empty-state">No threads yet. Be the first to post!</p>
                            ) : (
                                <table className="thread-table">
                                    <thead>
                                    <tr>
                                        <th>Thread</th>
                                        <th>Replies</th>
                                        <th>Last Post</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {homeData.recentActive.map((t) => (
                                        <tr key={t.id}>
                                            <td>
                                                <Link to={`/thread/${t.id}`} className="thread-link">
                                                    {t.pinned && <span className="badge badge--pin" title="Pinned">PINNED</span>}
                                                    {t.locked && <span className="badge badge--lock" title="Locked">LOCKED</span>}
                                                    {t.title}
                                                </Link>
                                                <div className="thread-meta">
                                                    by{" "}
                                                    <Link to={`/profile/${t.author_id}`}>
                                                        {t.author_name}
                                                    </Link>
                                                    {" in "}
                                                    <Link to={`/forum/${t.category_id}`}>
                                                        {t.category_name}
                                                    </Link>
                                                </div>
                                            </td>
                                            <td className="center">{Math.max(0, t.post_count - 1)}</td>
                                            <td className="last-post">
                                                {t.last_reply_at ? (
                                                    <>
                                                        <TimeAgo date={t.last_reply_at} />
                                                        <br />
                                                        <span className="last-post-by">
                                                                by{" "}
                                                            <Link to={`/profile/${t.last_reply_author_id}`}>
                                                                    {t.last_reply_by}
                                                                </Link>
                                                            </span>
                                                    </>
                                                ) : (
                                                    <span className="muted">No replies</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            )}
                        </section>

                        {/* Sidebar */}
                        <aside className="home-sidebar">
                            {/* New Threads */}
                            <section className="home-section">
                                <h2>New Threads</h2>
                                <ul className="sidebar-list">
                                    {homeData.newestThreads.map((t) => (
                                        <li key={t.id}>
                                            <Link to={`/thread/${t.id}`}>{t.title}</Link>
                                            <div className="sidebar-meta">
                                                <Link to={`/profile/${t.author_id}`}>
                                                    {t.author_name}
                                                </Link>
                                                {" · "}
                                                <TimeAgo date={t.created_at} />
                                            </div>
                                        </li>
                                    ))}
                                    {homeData.newestThreads.length === 0 && (
                                        <li className="empty-state">No threads yet.</li>
                                    )}
                                </ul>
                            </section>
                            {/* Recent Members */}
                            <section className="home-section">
                                <h2>New Members</h2>
                                <ul className="sidebar-list member-list">
                                    {homeData.recentMembers.map((m) => (
                                        <li key={m.id}>
                                            <Link to={`/profile/${m.id}`} className="member-link">
                                                <img
                                                    src={getAvatarUrl(m.avatar_url)}
                                                    alt=""
                                                    className="member-avatar-small"
                                                />
                                                <span>{m.display_name}</span>
                                            </Link>
                                            <div className="sidebar-meta">
                                                Joined <TimeAgo date={m.created_at} />
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        </aside>
                    </div>
                </>
            ) : (
                <p>Failed to load forum data.</p>
            )}
        </div>
    );
}